// PromotionalActivityReport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Input,
  Label,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { FaFilter } from "react-icons/fa";
import { Edit, Trash2 } from "react-feather";
import swal from "sweetalert";

/* ===== Your project utilities/components in ../import ===== */
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import {
  exportDataToPDF,
  convertDataCSVtoExcel,
  exportDataToExcel,
  convertDataCsvToXml,
} from "../import/Downloader";

/* ===== API calls (../import/ApiEndPoint/...) ===== */
import { _Get } from "../import/ApiEndPoint/ApiCalling";
import { Promotion_Report_List } from "../import/ApiEndPoint/Api";

/* ---------------- helpers ---------------- */
const prettyDate = (iso = "") => (iso || "").split("T")[0] || "";

/** Build an exportable CSV out of (rows, columns) */
function rowsToCSV(rows, columns) {
  const safe = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const headers = columns.map((c) => safe(c.label)).join(",");
  const body = rows
    .map((row) => columns.map((c) => safe(c.accessor(row))).join(","))
    .join("\n");

  return `${headers}\n${body}`;
}

/* ---------------- component ---------------- */
export default function PromotionalActivityReport() {
  const navigate = useNavigate();

  // permissions + master UI
  const [permissions, setPermissions] = useState({});
  const [masterShow, setMasterShow] = useState(false);

  // loading
  const [loading, setLoading] = useState(false);

  // data
  const [allData, setAllData] = useState([]);
  const [grouped, setGrouped] = useState({}); // customerName -> rows[]
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  // rows currently shown in the table
  const [rows, setRows] = useState([]);

  // backup of the "collapsed" default rows for quick restore
  const [collapsedRowsBackup, setCollapsedRowsBackup] = useState([]);

  // filters/search
  const [typeFilter, setTypeFilter] = useState("NA"); // "ProductWise" | "PercentageWise" | "AmountWise" | "NA"
  const [search, setSearch] = useState("");

  // download hover
  const [dlOpen, setDlOpen] = useState(false);
  const dlTimer = useRef(null);
  const onDownloadHover = (e) => {
    if (e.type === "mouseenter") {
      clearTimeout(dlTimer.current);
      setDlOpen(true);
    } else {
      dlTimer.current = setTimeout(() => setDlOpen(false), 150);
    }
  };

  // master switch handlers
  const handleParentSubmit = (e) => {
    e.preventDefault();
    const value = JSON.parse(localStorage.getItem("SuperadminIdByMaster")); // "id db"
    if (!value) return;
    const [id, db] = String(value).split(" ");
    loadAll(id, db);
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  /* columns to render + export */
  const columns = useMemo(
    () => [
      { key: "idx", label: "#", accessor: (_r, i) => i + 1 },
      {
        key: "activityName",
        label: "Promotional Activity Name",
        accessor: (r) => r?.ActivityType?.ActivityName || "",
      },
      {
        key: "customer",
        label: "Customer Name",
        accessor: (r) => r?.partyId?.CompanyName || "",
      },
      {
        key: "spName",
        label: "Sales Person name",
        accessor: (r) => r?.partyId?.created_by?.firstName || "",
      },
      {
        key: "spMobile",
        label: "Sales Person Mobile No.",
        accessor: (r) => r?.partyId?.created_by?.mobileNumber || "",
      },
      {
        key: "productName",
        label: "Product Name",
        accessor: (r) => r?.productName || "",
      },
      {
        key: "target",
        label: "Target",
        accessor: (r) => r?.Target ?? "",
      },
      {
        key: "achieved",
        label: "Achieved",
        accessor: (r) => r?.Achieved ?? "",
      },
      {
        key: "balance",
        label: "Balance",
        accessor: (r) =>
          typeof r?.Balance === "number"
            ? r.Balance.toFixed(2)
            : r?.Balance ?? "",
      },
      {
        key: "accessDone",
        label: "Access Done",
        accessor: (r) =>
          typeof r?.Balance === "number"
            ? r.Balance.toFixed(2)
            : r?.Balance ?? "",
      },
      {
        key: "status",
        label: "Status",
        accessor: (r) => r?.Status || "",
      },
      {
        key: "schemeAmount",
        label: "Scheme Recieved Amount",
        accessor: (r) => (r?.type === "AmountWise" ? r?.OfferAmount || 0 : 0),
      },
      {
        key: "schemePct",
        label: "Scheme Recieved Percentage",
        accessor: (r) =>
          r?.type === "PercentageWise" ? r?.OfferAmount || 0 : 0,
      },
      {
        key: "schemeProd",
        label: "Scheme Recieved Product Name",
        accessor: (r) => (r?.type === "ProductWise" ? r?.productName || 0 : 0),
      },
      {
        key: "schemeQty",
        label: "Scheme Recieved Quantity",
        accessor: (r) => (r?.type === "ProductWise" ? r?.OfferAmount || 0 : 0),
      },
    ],
    []
  );

  /* initial bootstrap */
  useEffect(() => {
    const perms = CheckPermission("Promotional Activity Report") || {};
    setPermissions(perms);

    const user = JSON.parse(localStorage.getItem("userData"));
    if (user?.rolename?.roleName === "MASTER") setMasterShow(true);

    if (user?.database) {
      loadAll(user?._id, user?.database);
    }
  }, []);

  const loadAll = async (id, db) => {
    setLoading(true);
    try {
      const res = await _Get(Promotion_Report_List, db);
      const list = res?.Promotion || [];

      // group by customer
      const groupedByCustomer = {};
      list.forEach((item) => {
        const cust = item?.partyId?.CompanyName || "Unknown";
        (groupedByCustomer[cust] = groupedByCustomer[cust] || []).push(item);
      });

      // collapsed default: choose "Monthly" if present else the first for each customer
      const collapsed = Object.keys(groupedByCustomer).map((cust) => {
        const arr = groupedByCustomer[cust];
        const monthly = arr.find(
          (x) => x?.ActivityType?.ActivityName === "Monthly"
        );
        return monthly || arr[0];
      });

      setAllData(list);
      setGrouped(groupedByCustomer);
      setRows(collapsed);
      setCollapsedRowsBackup(collapsed);
      setExpandedCustomer(null);
      setTypeFilter("NA");
      setSearch("");
    } finally {
      setLoading(false);
    }
  };

  /* clicking a row toggles that customer's expansion (only when not type-filtered) */
  const onRowClick = (row) => {
    if (typeFilter !== "NA") return; // expansion only in collapsed mode
    const cust = row?.partyId?.CompanyName || "Unknown";
    if (expandedCustomer === cust) {
      // collapse
      setRows(collapsedRowsBackup);
      setExpandedCustomer(null);
    } else {
      // expand
      setRows(grouped[cust] || []);
      setExpandedCustomer(cust);
    }
  };

  /* change promotion type filter */
  const onTypeChange = (val) => {
    setTypeFilter(val);
    setExpandedCustomer(null);

    if (!val || val === "NA") {
      setRows(collapsedRowsBackup);
      return;
    }

    const selected = allData.filter((item) => item?.type === val);
    setRows(selected);
  };

  /* search */
  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      columns.some((c, idx) => {
        if (c.key === "idx") return false;
        const v = c.accessor(r, idx);
        return (
          v !== undefined && v !== null && String(v).toLowerCase().includes(q)
        );
      })
    );
  }, [rows, search, columns]);

  /* exports */
  const buildCSV = () => rowsToCSV(filteredRows, columns);
  const exportPDF = async () => {
    const csv = buildCSV();
    await exportDataToPDF(csv, "PromotionalActivityReport");
  };
  const exportCSV = () => {
    const csv = buildCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", "PromotionalActivityReport.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const exportXLS = async () => {
    const csv = buildCSV();
    await convertDataCSVtoExcel(csv, "PromotionalActivityReport");
  };
  const exportXLSX = async () => {
    const csv = buildCSV();
    await exportDataToExcel(csv, "PromotionalActivityReport");
  };
  const exportXML = async () => {
    const csv = buildCSV();
    await convertDataCsvToXml(csv, "PromotionalActivityReport");
  };

  /* UI */
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <div className="page-wrap">
      <style>{`
        .page-wrap { padding: 8px; }

        .card-slim { border: 1px solid #e5e7eb; border-radius: 10px; padding: 10px; }
        .title { font-weight: 600; text-transform: uppercase; font-size: 16px; margin: 6px 0 10px; }
        .muted { color: #64748b; font-size: 12px; }
        .btn-compact { height: 28px; padding: 0 10px; display: inline-flex; align-items: center; gap: 6px; font-weight: 600; }
        .click { cursor: pointer; }
        .center { text-align: center; }

        /* compact table */
        .table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .table th, .table td { padding: 6px 8px; border: 1px solid #e5e7eb; text-align: center; white-space: nowrap; }
        .table th { background: #f8fafc; font-weight: 700; }

        /* toolbar */
        .tools { display: flex; gap: 8px; justify-content: flex-end; align-items: center; flex-wrap: wrap; }
        .download-menu { position: absolute; right: 0; top: 30px; z-index: 10; background: #fff; border: 1px solid #0a58ca; }
        .download-item { padding: 6px 10px; cursor: pointer; font-size: 12px; }
        .download-item:hover { background: #f1f5f9; }
      `}</style>

      {/* Header */}
      <Card className="card-slim">
        <Row
          className="align-items-center"
          style={{ marginLeft: 3, marginRight: 3 }}
        >
          <Col md="6" sm="12">
            <div className="title">Promotional Activity Report</div>
            <div className="muted">
              <a href="/#/dashboard" style={{ color: "green" }}>
                Home
              </a>{" "}
              &nbsp;/&nbsp; Reports
            </div>
          </Col>

          <Col
            md="6"
            sm="12"
            className="tools"
            style={{ position: "relative" }}
          >
            {masterShow && (
              <SuperAdminUI
                onDropdownChange={handleDropdownChange}
                onSubmit={handleParentSubmit}
              />
            )}

            {/* Link back to PromotionalActivity page */}
            <Button
              color="secondary"
              className="btn-compact"
              onClick={() =>
                navigate("/app/ajgroup/account/PromotionalActivity")
              }
              title="Go to Promotional Activity"
            >
              Back to Promotional Activity
            </Button>

            {/* Type filter */}
            <div>
              <Label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 2,
                  display: "block",
                }}
              >
                Promotion Type
              </Label>
              <Input
                type="select"
                value={typeFilter}
                onChange={(e) => onTypeChange(e.target.value)}
                style={{ height: 28, fontSize: 12, width: 180 }}
              >
                <option value="NA">-- Select --</option>
                <option value="ProductWise">ProductWise</option>
                <option value="PercentageWise">PercentageWise</option>
                <option value="AmountWise">AmountWise</option>
              </Input>
            </div>

            {/* Search */}
            <div>
              <Label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 2,
                  display: "block",
                }}
              >
                Search
              </Label>
              <Input
                placeholder="Type to filter…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ height: 28, fontSize: 12, width: 200 }}
              />
            </div>

            {/* Download */}
            <span
              onMouseEnter={onDownloadHover}
              onMouseLeave={onDownloadHover}
              style={{ position: "relative" }}
            >
              <Button color="light" className="btn-compact">
                <ImDownload size={14} /> Download
              </Button>
              {dlOpen && (
                <div className="download-menu">
                  <div className="download-item center" onClick={exportPDF}>
                    .PDF
                  </div>
                  <div className="download-item center" onClick={exportCSV}>
                    .CSV
                  </div>
                  <div className="download-item center" onClick={exportXLS}>
                    .XLS
                  </div>
                  <div className="download-item center" onClick={exportXLSX}>
                    .XLSX
                  </div>
                  <div className="download-item center" onClick={exportXML}>
                    .XML
                  </div>
                </div>
              )}
            </span>
          </Col>
        </Row>

        {/* Table */}
        <div style={{ overflowX: "auto", marginTop: 8 }}>
          <Table bordered className="table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, idx) => (
                <tr
                  key={r?._id || idx}
                  className="click"
                  onClick={() => onRowClick(r)}
                >
                  {columns.map((c, i) => (
                    <td key={c.key}>
                      {c.key === "idx" ? idx + 1 : c.accessor(r, i)}
                    </td>
                  ))}
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="center muted">
                    No data
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
